import {
  Box,
  BriefcaseIcon,
  Card,
  Flex,
  Image,
  LocationDisplay,
  SimpleGrid,
  Text,
  UserAvatar,
} from 'components'
import { useWorkspace } from 'components/providers'
import { generatePath, useNavigate, useParams } from 'react-router'
import { forceLight, useBreakpoint, useTheme, useUser, useWorkspaceRecord } from 'utils'
import { FacilityRecord, useFacilities } from 'utils/facilityUtils'
import noFacilityDark from '../assets/emptyStates/no_facilities_dark.svg'
import noFacilityLight from '../assets/emptyStates/no_facilities_light.svg'
import CreateNewButton from '../components/CreateNewButton'
import EmptyState from '../components/EmptyState'
import ResourceUpsell from '../components/subscription/ResourceUpsell'
import { RoutePath } from '../utils/routing'

export default function FacilitiesPage() {
  const { subscription } = useWorkspace()
  const { facilities } = useFacilities()
  const { theme } = useTheme()
  const isMobile = useBreakpoint() < 2

  const { plan, selectedResource } = subscription || {}
  if (plan === 'basic' && selectedResource === 'event') {
    return (
      <Flex flexDirection="column" gap={4} maxWidth="1100px" marginX={{ base: 4, md: 'auto' }}>
        <Text textStyle="h3" pt={4}>
          Facilities
        </Text>
        <ResourceUpsell variant="two" resourceName="facility" />
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
      overflowY="auto"
      overflowX="hidden"
    >
      <Text textStyle="h3" pt={2}>
        Facilities
      </Text>
      {facilities?.length == 0 && (
        <EmptyState
          title="No Facilities"
          description="No facilities created yet."
          iconSrc={theme === 'light' ? noFacilityLight : noFacilityDark}
          iconProps={{ height: '176px' }}
          button={<CreateNewButton />}
        />
      )}

      <FacilitiesGrid facilities={facilities!} />
    </Flex>
  )
}

interface Props {
  facilities: FacilityRecord[]
}

function FacilitiesGrid({ facilities }: Props) {
  return (
    <SimpleGrid gap={5} columns={{ base: 0, lg: 2, xl: 3 }}>
      {facilities?.map(facility => (
        <FacilityCard key={facility.id} {...facility} />
      ))}
    </SimpleGrid>
  )
}

function FacilityCard(props: FacilityRecord) {
  const workspaceIdParam = useParams().workspaceId || ''
  const { location, id, title, workspaceId, imageUrl, managerId } = props
  const workspace = useWorkspaceRecord(workspaceId)
  const manager = useUser(managerId)
  const navigate = useNavigate()

  function goToFacility(id: string, workspaceId: string) {
    navigate(generatePath(RoutePath.FacilityInfo, { workspaceId, id }))
  }

  return (
    <Box
      {...forceLight}
      borderRadius="xl"
      boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.10)"
      overflow="hidden"
    >
      <Card
        cursor="pointer"
        onClick={() => goToFacility(id, workspaceId)}
        header={
          <Image height="175px" objectFit="cover" src={imageUrl} alt={title} draggable="false" />
        }
        body={
          <Flex align="center" justify="space-between" overflow="hidden">
            <Flex direction="column" gap={1} overflow="hidden">
              <Text
                textStyle="bodyLarge"
                color={title ? 'inherit' : 'gray'}
                truncate
                textOverflow="ellipsis"
              >
                {title || '--'}
              </Text>
              {workspaceIdParam === 'all' && (
                <Flex alignItems="center" gap={1}>
                  <BriefcaseIcon fontSize="sm" type="outline" />
                  <Text textStyle="bodyXSmall" opacity={0.7} truncate textOverflow="ellipsis">
                    {workspace?.title}
                  </Text>
                </Flex>
              )}

              <LocationDisplay
                location={location}
                iconProps={{ size: 'sm' }}
                textProps={{ textStyle: 'bodyMedium', fontWeight: 'normal', opacity: 1 }}
              />
            </Flex>

            <UserAvatar user={manager} />
          </Flex>
        }
        bodyProps={{ p: 5 }}
      />
    </Box>
  )
}
